import React, { type Dispatch, type SetStateAction, useEffect } from 'react';
import styled from 'styled-components';
import { zIndex } from 'variables/layers';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import type { AdvisorChatViewModel } from 'autogen/swagger/all';
import { dialogCss, dialog } from './chatConstants';
import ChatSelectContainer from './chatSelectContainer';
import { BorderRadiusLarge, BorderRadiusMedium, ColorNeutralWhite, Spacing8 } from 'autogen/design-tokens/tokens';
import { Button } from 'components/Button';
import Icon from 'components/Icon';
import { customScrollbar } from 'modules/helpers/mixins';

const Dialog = styled.div`
    position: fixed;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 90%;
    display: flex;
    flex-direction: column;
    z-index: ${zIndex.dialog};
    background: ${ColorNeutralWhite};
    border-top-left-radius: ${BorderRadiusLarge};
    border-top-right-radius: ${BorderRadiusLarge};

    ${dialog.breakpoint} {
        ${dialogCss}
        border-radius: ${BorderRadiusMedium};
        overflow: hidden;
        box-shadow: ${dialog.shadow};
    }
`;

const Header = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: ${Spacing8};
`;

const ScrollContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 1px;
    overflow: auto;
    ${customScrollbar};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing8};
    flex: 1 0 auto;
    min-height: 1px;
`;

interface IChatDialogProps {
    showSelect: boolean;
    minimizeDialog: () => void;
    minimizeChat: () => void;
    sections: AdvisorChatViewModel['sections'];
    setChatOpen: Dispatch<SetStateAction<boolean>>;
    setShowDialog: Dispatch<SetStateAction<boolean>>;
}

function ChatDialog(props: IChatDialogProps): JSX.Element | null {
    const { showSelect, minimizeDialog, minimizeChat, sections, setChatOpen, setShowDialog } = props;
    const scrollRef = React.createRef<any>();

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return undefined;

        if (showSelect && window.matchMedia(`(max-width: ${dialog.breakpointPixels})`).matches) {
            disableBodyScroll(scrollContainer);
        } else {
            enableBodyScroll(scrollContainer);
        }

        return (): void => {
            enableBodyScroll(scrollContainer);
        };
    }, [scrollRef, showSelect]);

    if (!showSelect) {
        return null;
    }

    return (
        <Dialog>
            <Header>
                <Button.WithIcon onClick={minimizeChat} icon={<Icon type="Close" size={16} />} variant="primaryText" />
            </Header>
            <ScrollContent ref={scrollRef}>
                <Content>
                    {sections.map((section, i) => (
                        <ChatSelectContainer
                            key={i}
                            section={section}
                            minimizeDialog={minimizeDialog}
                            minimizeChat={minimizeChat}
                            setChatOpen={setChatOpen}
                            setShowDialog={setShowDialog}
                        />
                    ))}
                </Content>
            </ScrollContent>
        </Dialog>
    );
}

export default ChatDialog;
